import { useQuery } from "@apollo/client";
import moment from "moment";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Redirect } from "react-router-dom";
import {
  GetMeetingNewPageQueryQuery,
  GetMeetingNewPageQueryQueryVariables,
  MeetingHeaderFragmentFragment,
  MeetingViewMeetingNodeNewPageFragmentFragment,
} from "types/graphql-schema";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import cache, { currentUserVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppError from "@components/error/error";
import GraphqlError from "@components/error/graphql-error";
import Layout from "@components/layout/layout";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
  removeCurrentYear,
} from "@helpers/helpers";

import getMeetingQuery from "../graphql/get-meeting-query";
import { MeetingHeaderFragment } from "../graphql/get-previous-meetings-query";
import MeetingDetails from "./details";
import MeetingBreadcrumb from "./meeting-breadcrumb";
import { MeetingPageContext } from "./meeting-page-context";
import MeetingPageTOC from "./meeting-page-toc";
import {
  TocMeeting,
  meetingRowDomId,
  useActiveMeetingId,
} from "./meeting-page-toc-helpers";
import MeetingViewSidebar from "./meeting-sidebar/meeting-view-sidebar";
import {
  MeetingTOCDockContext,
  useTocHasRoom,
} from "./meeting-toc-dock-context";
import MeetingTopics from "./meeting-topics";
import MeetingPageVisibilityRefreshDetection from "./meeting-visibility-refresh-detection";
import MeetingPrepCard from "./prep/meeting-prep-card";
import MeetingSummary from "./prep/summary";
import { defaultTopicTitle } from "./topic";

export const MeetingPageContent = ({
  meeting,
  meetingGroupId,
  isVideoDeepLinkTarget = true,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  meetingGroupId: number;
  isVideoDeepLinkTarget?: boolean;
}) => {
  const {
    autoFocusDefaultTopic,
    animateTopicLayout,
    isInSidebar,
    isInExtension,
  } = useContext(MeetingPageContext);
  const { uiPreferenceCache } = useUiPreferenceCache();

  // AI prep nudge for formal 1-on-1s that haven't been touched yet. Renders
  // beneath the Topics section (it doesn't replace it) until the user clicks
  // "Prepare with AI" on the card, which dismisses it for this specific meeting
  // (persisted in oneononePrepCardDismissedMeetingIds — meeting-scoped, so it
  // still appears on later empty 1-on-1s with the same subject).
  // Only on the full meeting page / profile 1-on-1 tab — the card targets those
  // wide layouts, so skip it in the embedded meetings sidebar and extension.
  const meetingHasEnded =
    !!meeting.endDatetime && moment().isAfter(moment(meeting.endDatetime));
  const topics = meeting.agendaTopics.map((occurrence) => occurrence.topic);
  const subjectId = meeting.subject?.id ?? null;
  const isEmptyOneonone =
    !!meeting.meetingGroup?.isFormalOneonone &&
    !meetingHasEnded &&
    subjectId !== null &&
    (topics.length === 0 ||
      (topics.length === 1 && topics[0].title === defaultTopicTitle));
  // "Prepare with AI" hides the card for that specific meeting instance.
  const meetingDismissed =
    uiPreferenceCache.oneononePrepCardDismissedMeetingIds.includes(meeting.id);
  const showEmptyCards =
    isEmptyOneonone && !meetingDismissed && !isInSidebar && !isInExtension;

  const currentUser = currentUserVar();
  const directReportIds = new Set(
    (currentUser.directReports
      ? assertEdgesNonNull(currentUser.directReports)
      : []
    ).map((r) => r.id),
  );
  // The direct report this 1-on-1 is with (from the manager's view), if any — used to
  // offer report onboarding on the prep card. Only for manager↔report 1-on-1s.
  const onboardingReportUser = meeting.isManagerAndReportOneonone
    ? assertEdgesNonNullWithStringId(meeting.participants)
        .map((participant) => participant.user)
        .find(
          (user) =>
            !!user &&
            user.id !== currentUser.id &&
            directReportIds.has(user.id),
        )
    : null;

  return (
    <>
      <MeetingDetails meeting={meeting} />
      <div className="flex flex-col">
        <MeetingSummary
          meeting={meeting}
          meetingGroupId={meetingGroupId}
          isCurrentMeeting
          autoOpenVideoTranscriptModal={isVideoDeepLinkTarget}
        />
        {/* MeetingSummary hides itself for meetings that haven't ended yet
            when there's no generated summary, but renders if a summary is
            present (e.g. team finished the meeting early). */}
        <div className="mt-2 mb-12 flex flex-col gap-3">
          <MeetingTopics
            meeting={meeting}
            opensTopicsInSidebar={false}
            currentPageMeeting={meeting}
            autoFocusDefaultTopic={autoFocusDefaultTopic ?? true}
            // When meetings stack in a paginated list, inserting one above
            // shifts these topics — skip the layout animation so they don't
            // all slide into place at once.
            animateTopicLayout={animateTopicLayout ?? true}
          />
          {/* Untouched 1-on-1 (single empty topic): nudge AI prep beneath it. */}
          {showEmptyCards && (
            <MeetingPrepCard
              meetingId={meeting.id}
              report={
                onboardingReportUser
                  ? {
                      id: onboardingReportUser.id,
                      name: onboardingReportUser.name,
                    }
                  : null
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

type PreviousMeetingInfo = {
  id: number;
  startDatetime: string | null;
} | null;

// A single meeting in the paginated meeting-page list: fetches the meeting and
// renders its content; the prev/next arrows paginate the surrounding list.
const MeetingPageListRow = ({
  meetingId,
  onNavigate,
  autoFocusDefaultTopic,
  animateTopicLayout,
  scrollOnLoad,
  onScrolled,
  onLoaded,
  isVideoDeepLinkTarget,
  placeholderDate,
  loadingMeetingId,
}: {
  meetingId: number;
  loadingMeetingId: number | null;
  onNavigate: (
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => void;
  autoFocusDefaultTopic: boolean;
  animateTopicLayout: boolean;
  // True when this row is the target of a pagination navigation and should
  // scroll itself into view once its meeting has loaded.
  scrollOnLoad: boolean;
  onScrolled: () => void;
  // Called once this row's meeting has loaded; receives the meeting itself
  // (for the TOC) plus the previous meeting info (id + startDatetime, or null)
  // so the parent can render the "Load previous meeting" button at the bottom
  // of the list.
  onLoaded?: (info: {
    meeting: TocMeeting;
    previousMeeting: PreviousMeetingInfo;
  }) => void;
  isVideoDeepLinkTarget: boolean;
  // Pre-known meeting date used in the loading placeholder before the row's
  // own GraphQL query has resolved (e.g. from a TOC click or auto-load
  // where the date is already in the parent's state).
  placeholderDate?: string | null;
}) => {
  // Read the Ask AI sidebar state from the enclosing MeetingPage provider so we
  // can forward it into this row's own provider below — the sticky meeting
  // header (details.tsx) needs it to match Layout.Main's padding breakpoint.
  const { meetingSidebarExpanded } = useContext(MeetingPageContext);
  const { data, loading } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, { variables: { meetingId } });
  const meeting = data?.meeting;
  const rowRef = useRef<HTMLDivElement | null>(null);
  const isLoaded = !loading && !!meeting && !!meeting.meetingGroup;

  // Scroll into view only once the meeting content has loaded. Scrolling while
  // the loading placeholder is showing would land on the spinner and then jump
  // as the (differently sized) content swaps in.
  useEffect(() => {
    if (scrollOnLoad && isLoaded && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      onScrolled();
      // Layout above can keep settling after the initial render (editors
      // mounting, etc.), so re-fire once the smooth scroll typically has
      // completed to correct for any drift.
      const t = window.setTimeout(() => {
        rowRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 700);
      return () => window.clearTimeout(t);
    }
  }, [scrollOnLoad, isLoaded, onScrolled]);

  const previousMeeting = meeting?.previousMeeting ?? null;
  useEffect(() => {
    if (isLoaded && meeting) {
      onLoaded?.({
        meeting: {
          id: meeting.id,
          startDatetime: meeting.startDatetime ?? null,
          meetingGroupId: meeting.meetingGroupId,
        },
        previousMeeting: previousMeeting
          ? {
              id: previousMeeting.id,
              startDatetime: previousMeeting.startDatetime ?? null,
            }
          : null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, previousMeeting?.id]);

  return (
    // min-h-[85vh] ensures each meeting fills the majority of the viewport so
    // that the next (older) meeting's date header just peeks in at the bottom.
    // Short meetings (few/no topics) still get the full reserved space — the
    // empty area below the content is intentional.
    <div
      ref={rowRef}
      id={meetingRowDomId(meetingId)}
      className="js-test-meeting-row scroll-mt-4 min-h-[85vh]"
    >
      {loading || !meeting || !meeting.meetingGroup ? (
        <div className="flex min-h-[290px] items-center justify-center">
          <div className="flex items-center gap-2 bg-gray-100 rounded-md p-2">
            <Loading mini size="4" />
            <span className="text-sm text-gray-700">
              {placeholderDate
                ? `Loading meeting: ${removeCurrentYear(
                    moment(placeholderDate).format("ddd, ll"),
                  )}`
                : "Loading meeting…"}
            </span>
          </div>
        </div>
      ) : (
        <MeetingPageContext.Provider
          value={{
            isInSidebar: false,
            isInExtension: false,
            autoFocusDefaultTopic,
            animateTopicLayout,
            onNavigateToMeeting: onNavigate,
            loadingMeetingId,
            meetingSidebarExpanded,
          }}
        >
          <MeetingPageContent
            meeting={meeting}
            meetingGroupId={meeting.meetingGroup.id}
            isVideoDeepLinkTarget={isVideoDeepLinkTarget}
          />
        </MeetingPageContext.Provider>
      )}
    </div>
  );
};

const formatLoadPreviousDate = (startDatetime: string | null): string => {
  if (!startDatetime) return "";
  const t = moment(startDatetime);
  const timeFormat = t.minute() === 0 ? "ha" : "h:mma";
  return t.format(`MMM D, ${timeFormat}`);
};

// The meeting page renders its meeting in a list so the prev/next arrows can
// page through the series in place (inserting the older meeting below / the
// newer one above) instead of navigating to a new page. Keyed by the routed
// meeting id so navigating to a different meeting resets the list.
const MeetingPageList = ({
  meetingId,
  previousMeetingId,
}: {
  meetingId: number;
  previousMeetingId: number | null;
}) => {
  // The TOC renders into a dock element owned by MeetingPage (rendered as a
  // sibling of the meeting sidebar). See MeetingTOCDockContext.
  const { dockEl: tocDock } = useContext(MeetingTOCDockContext);
  // Start with just the routed meeting; the previous one is loaded in below it
  // only once the routed meeting has loaded (see showPreviousMeeting), so the
  // two don't load at the same time. This component is keyed by the routed
  // meeting id upstream, so it resets when the user navigates to a different
  // meeting.
  const [displayedIds, setDisplayedIds] = useState<number[]>([meetingId]);
  // Scroll the routed meeting to the top once it loads. Re-seeds on navigation
  // (keyed upstream) — e.g. clicking the previous meeting's date link (which
  // loads in below) scrolls back up to that meeting instead of leaving the
  // viewport scrolled down.
  const [pendingScrollId, setPendingScrollId] = useState<number | null>(
    meetingId,
  );
  // The previous-meeting info for each loaded row, captured when the row
  // finishes loading. The entry for the bottom-most row drives the
  // "Load previous meeting: <date>" button at the end of the list.
  const [previousMeetingByRow, setPreviousMeetingByRow] = useState<
    Record<number, PreviousMeetingInfo>
  >({});
  // Each row's own meeting info, captured when the row finishes loading,
  // so the TOC can render a line button for each displayed meeting.
  const [meetingByRow, setMeetingByRow] = useState<Record<number, TocMeeting>>(
    {},
  );
  // Dates we already know for meetings whose rows haven't finished fetching
  // yet (e.g. from a TOC click target or another row's previousMeeting field).
  // The row uses this to show "Loading meeting: <date>" instead of a bare
  // spinner during the GraphQL fetch.
  const [placeholderDates, setPlaceholderDates] = useState<
    Record<number, string | null>
  >({});
  const recordPlaceholderDate = useCallback(
    (id: number, date?: string | null) => {
      if (!date) return;
      setPlaceholderDates((prev) =>
        prev[id] === date ? prev : { ...prev, [id]: date },
      );
    },
    [],
  );

  // Once the routed meeting has loaded, load its previous meeting in below it
  // (TF-1450). Formal 1-on-1s redirect to the profile tab before reaching this
  // list, so this only affects group meetings; their prev/next arrows then page
  // through the rest of the series. No-op once the user has paginated.
  const showPreviousMeeting = useCallback(() => {
    if (previousMeetingId === null || previousMeetingId === meetingId) return;
    setDisplayedIds((prev) =>
      prev.length === 1 && prev[0] === meetingId
        ? [meetingId, previousMeetingId]
        : prev,
    );
  }, [previousMeetingId, meetingId]);

  const handleRowLoaded = useCallback(
    (
      rowMeetingId: number,
      info: { meeting: TocMeeting; previousMeeting: PreviousMeetingInfo },
    ) => {
      setPreviousMeetingByRow((prev) =>
        prev[rowMeetingId]?.id === info.previousMeeting?.id
          ? prev
          : { ...prev, [rowMeetingId]: info.previousMeeting },
      );
      setMeetingByRow((prev) =>
        prev[rowMeetingId]?.startDatetime === info.meeting.startDatetime
          ? prev
          : { ...prev, [rowMeetingId]: info.meeting },
      );
      // Remember the previous meeting's date so when it (or it eventually
      // gets) added to displayedIds, its row's loading placeholder can show
      // the date.
      if (info.previousMeeting?.startDatetime) {
        recordPlaceholderDate(
          info.previousMeeting.id,
          info.previousMeeting.startDatetime,
        );
      }
      if (rowMeetingId === meetingId) showPreviousMeeting();
    },
    [meetingId, showPreviousMeeting, recordPlaceholderDate],
  );

  const { activeMeetingId, setActiveMeetingId } =
    useActiveMeetingId(displayedIds);

  const handleScrollToMeeting = useCallback(
    (id: number) => {
      setActiveMeetingId(id);
      const scrollToRow = () => {
        const el = document.getElementById(meetingRowDomId(id));
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      };
      scrollToRow();
      // Editors / images mount after the initial render and can shift the
      // target's position mid-animation. Re-fire once the smooth scroll has
      // typically completed so any drift is corrected.
      window.setTimeout(scrollToRow, 700);
    },
    [setActiveMeetingId],
  );

  // Insert a meeting clicked from the TOC into displayedIds at the right spot
  // chronologically (newest first), then scroll to it. Used for meetings that
  // aren't already loaded — either an adjacent one (chevron click) or one
  // several steps away (line click).
  const handleLoadMeeting = useCallback(
    (target: TocMeeting) => {
      setActiveMeetingId(target.id);
      recordPlaceholderDate(target.id, target.startDatetime);
      setDisplayedIds((prev) => {
        if (prev.includes(target.id)) return prev;
        const targetTime = target.startDatetime
          ? moment(target.startDatetime)
          : null;
        let insertIndex = prev.length;
        if (targetTime) {
          for (let i = 0; i < prev.length; i++) {
            const m = meetingByRow[prev[i]];
            if (
              m?.startDatetime &&
              targetTime.isAfter(moment(m.startDatetime))
            ) {
              insertIndex = i;
              break;
            }
          }
        }
        return [
          ...prev.slice(0, insertIndex),
          target.id,
          ...prev.slice(insertIndex),
        ];
      });
      setPendingScrollId(target.id);
    },
    [meetingByRow, setActiveMeetingId, recordPlaceholderDate],
  );

  const displayedMeetings = displayedIds
    .map((id) => meetingByRow[id])
    .filter((m): m is TocMeeting => !!m);

  const handleNavigate = (
    sourceId: number,
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => {
    setDisplayedIds((prev) => {
      if (prev.includes(target.id)) return prev;
      const idx = prev.indexOf(sourceId);
      const insertAt =
        idx === -1 ? prev.length : position === "after" ? idx + 1 : idx;
      return [...prev.slice(0, insertAt), target.id, ...prev.slice(insertAt)];
    });
    setPendingScrollId(target.id);
  };

  const bottomRowId = displayedIds[displayedIds.length - 1];
  const bottomRowPrevious = previousMeetingByRow[bottomRowId] ?? null;
  const canLoadAnotherPreviousMeeting =
    !!bottomRowPrevious && !displayedIds.includes(bottomRowPrevious.id);
  // A click-loaded meeting is "loading" while its row's data is still being
  // fetched — i.e. it's the pending scroll target but its meeting info
  // isn't in meetingByRow yet. Surfaced to the TOC and to each row's
  // context so inline chevrons can show a spinner on small screens too.
  const loadingMeetingId =
    pendingScrollId !== null && !meetingByRow[pendingScrollId]
      ? pendingScrollId
      : null;

  const handleClickLoadPreviousMeeting = () => {
    if (!bottomRowPrevious) return;
    setDisplayedIds((prev) =>
      prev.includes(bottomRowPrevious.id)
        ? prev
        : [...prev, bottomRowPrevious.id],
    );
  };

  // Auto-load the next older meeting as soon as the user has scrolled into
  // the current bottommost row — turns the "Load previous meeting" button
  // into an infinite-scroll experience. The cascade naturally pauses while
  // the newly-appended row is still fetching (bottomRowPrevious is null
  // until its data lands) and only resumes when the user scrolls into the
  // new bottom.
  useEffect(() => {
    if (
      activeMeetingId !== null &&
      activeMeetingId === bottomRowId &&
      canLoadAnotherPreviousMeeting
    ) {
      handleClickLoadPreviousMeeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMeetingId, bottomRowId, canLoadAnotherPreviousMeeting]);

  return (
    <div className="relative">
      {/* The TOC's DOM lives inside the dock owned by MeetingPage (a sibling
          of the meeting right-sidebar). Portal so the TOC keeps its data
          (displayedMeetings, activeMeetingId, etc.) wired through props from
          this list, while its position is decided alongside the sidebar. */}
      {/* The TOC self-hides when the subject's series has nothing to navigate
          to (a lone draft / one-off) — see MeetingPageTOC. */}
      {tocDock !== null &&
        displayedMeetings.length > 0 &&
        createPortal(
          <MeetingPageTOC
            displayedMeetings={displayedMeetings}
            activeMeetingId={activeMeetingId}
            onScrollToMeeting={handleScrollToMeeting}
            onLoadMeeting={handleLoadMeeting}
            loadingMeetingId={loadingMeetingId}
          />,
          tocDock,
        )}
      <div
        className={classNames(
          // gap-[200px] keeps a substantial space between stacked meeting
          // dates so that even when a meeting's notes are long, it's clear
          // where one meeting ends and the next (older) one begins.
          "flex flex-col gap-[200px]",
          // 48px below the load-more button (or the final meeting, if there
          // is none) so it isn't flush with the viewport edge.
          canLoadAnotherPreviousMeeting || displayedIds.length > 1
            ? "pb-12"
            : "",
        )}
      >
        {displayedIds.map((id) => (
          <MeetingPageListRow
            key={id}
            meetingId={id}
            // Keep focusing the routed meeting's default topic even when the
            // previous meeting is stacked below it.
            autoFocusDefaultTopic={id === meetingId}
            animateTopicLayout={displayedIds.length === 1}
            scrollOnLoad={id === pendingScrollId}
            onScrolled={() => setPendingScrollId(null)}
            onLoaded={(info) => handleRowLoaded(id, info)}
            isVideoDeepLinkTarget={id === meetingId}
            placeholderDate={placeholderDates[id] ?? null}
            loadingMeetingId={loadingMeetingId}
            onNavigate={(target, position) =>
              handleNavigate(id, target, position)
            }
          />
        ))}
        {canLoadAnotherPreviousMeeting && (
          <div className="flex justify-center mt-8">
            <Button
              theme={buttonTheme.default}
              size={ButtonSize.medium}
              onClick={handleClickLoadPreviousMeeting}
            >
              Load previous meeting:{" "}
              {formatLoadPreviousDate(bottomRowPrevious.startDatetime)}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const meetingPageRefreshInterval = 5 * 60 * 1000; // 5 minutes

const MeetingPage = ({
  meetingId,
  hideSidebar = false,
  disableOneononeRedirect = false,
  onNavigateToMeeting,
}: {
  meetingId: number;
  hideSidebar?: boolean;
  // When embedded (e.g. inside the profile 1-on-1 tab) we must not redirect
  // formal 1-on-1s back to the profile tab — that would loop.
  disableOneononeRedirect?: boolean;
  // When embedded, the prev/next meeting arrows paginate within the embedder
  // instead of navigating to a new meeting page.
  onNavigateToMeeting?: (
    target: { id: number; meetingGroupId: number },
    position: "before" | "after",
  ) => void;
}) => {
  const [sidebarMeetingView, setSidebarMeetingView] = useState<
    "overview" | "load-previous-meeting" | number
  >("overview");

  // The DOM node the floating meeting TOC portals into. Owned here so its
  // position is decided alongside the sidebar in ONE place. Callback ref
  // (via useState) so the portal re-evaluates once the node mounts.
  const [tocDockEl, setTocDockEl] = useState<HTMLDivElement | null>(null);
  const hasTocRoom = useTocHasRoom(tocDockEl);
  const { uiPreferenceCache } = useUiPreferenceCache();
  const meetingSidebarExpanded =
    !hideSidebar && !!uiPreferenceCache.meetingSidebarExpanded;

  const { loading, error, data, refetch } = useQuery<
    GetMeetingNewPageQueryQuery,
    GetMeetingNewPageQueryQueryVariables
  >(getMeetingQuery, {
    variables: { meetingId },
  });

  const meeting = data?.meeting;
  const meetingGroup = meeting?.meetingGroup;
  const title = meeting?.title || meetingGroup?.title || "";
  useDocumentTitle(title);

  const hasError = error || !data || !meeting || !meetingGroup;

  // try to get data from cache so we can render meeting header asap
  const meetingCacheId = cache.identify({
    __typename: "MeetingNode",
    id: meetingId,
  });
  const meetingHeaderCache = cache.readFragment<MeetingHeaderFragmentFragment>({
    id: meetingCacheId,
    fragmentName: "MeetingHeaderFragment",
    fragment: MeetingHeaderFragment,
  });
  const meetingHeader = meeting || meetingHeaderCache;

  // Formal 1-on-1s are now surfaced on the subject's profile "1-on-1" tab
  // rather than on a standalone meeting page (TF-1416). Redirect once the
  // meeting has loaded and we know its subject. Only the full-page meeting
  // route renders MeetingPage; the extension and embedded sidebar views use
  // MeetingPageContent directly, so they are unaffected.
  if (
    !disableOneononeRedirect &&
    meetingGroup?.isFormalOneonone &&
    meeting?.subject?.id
  ) {
    return (
      <Redirect to={`/profile/${meeting.subject.id}/oneonone/${meeting.id}`} />
    );
  }

  return (
    <>
      <MeetingPageVisibilityRefreshDetection refetch={refetch} />
      <MeetingPageContext.Provider
        value={{
          sidebarMeetingView,
          setSidebarMeetingView,
          isInSidebar: false,
          isInExtension: false,
          onNavigateToMeeting,
          meetingSidebarExpanded,
        }}
      >
        <MeetingTOCDockContext.Provider
          value={{
            dockEl: tocDockEl,
            hasRoom: hasTocRoom,
          }}
        >
          <Layout.Header
            rightSideClassName="gap-1 @3xl/layout-header:gap-2"
            sidebarExpandedUiPreferenceKey={
              hideSidebar ? undefined : "meetingSidebarExpanded"
            }
            title={<MeetingBreadcrumb meeting={meetingHeader} />}
          />

          <Layout.Container>
            <Layout.Main
              width="md"
              className="scrollbar-minimal"
              sidebarExpandedUiPreferenceKey={
                hideSidebar ? undefined : "meetingSidebarExpanded"
              }
            >
              {!data && loading ? (
                <div className="w-full">
                  <Loading />
                </div>
              ) : error ? (
                <GraphqlError
                  error={error}
                  whatDidNotWork="The meeting could not be loaded."
                />
              ) : hasError ? (
                <div>
                  <AppError title="The meeting could not be loaded." />
                </div>
              ) : (
                <MeetingPageList
                  key={meeting.id}
                  meetingId={meeting.id}
                  previousMeetingId={meeting.previousMeeting?.id ?? null}
                />
              )}
            </Layout.Main>
            {!hideSidebar && meeting && (
              <MeetingViewSidebar meeting={meeting} />
            )}
            {/* TOC dock — anchored to the sidebar's left edge when the sidebar
                is visible, otherwise pinned 16px from the viewport's right
                edge. One source of truth for TOC positioning instead of two
                coupled offsets (sidebar + TOC) that had to be kept in sync
                across breakpoints. Widths match `widthBreakpointClassName` in
                Layout: w-96 (24rem), xl=w-120 (30rem), 2xl=w-144 (36rem). */}
            {meeting && (
              <div
                ref={setTocDockEl}
                className={classNames(
                  // Only show the floating TOC once it actually fits in the
                  // gutter beside the reading column (see useTocHasRoom).
                  // Below that the inline chevrons + calendar dropdown in the
                  // meeting header take over (see
                  // previous-meetings-popover-panel.tsx).
                  hasTocRoom ? "block" : "hidden",
                  // Anchored 16px below Layout.Header (which is 56px tall).
                  "fixed top-[72px] z-30",
                  meetingSidebarExpanded
                    ? "right-[calc(24rem+1rem)] xl:right-[calc(30rem+1rem)] 2xl:right-[calc(36rem+1rem)]"
                    : "right-4",
                )}
              />
            )}
          </Layout.Container>
        </MeetingTOCDockContext.Provider>
      </MeetingPageContext.Provider>
    </>
  );
};

export default MeetingPage;
